import { ArcBaseContent } from '@src/core/components';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedHistoryOrder } from '../../redux';
import { ArcBox, ArcCellInfo, ArcIconButton, ArcStack, ArcText, ArcTooltip } from '@src/core/ui';
import { ArcGoogleMap, setMapLocation } from '@src/core/controllers';
import { Route, Routes, useNavigate } from 'react-router-dom';
import HistoryMap from './map';
import {
  arcDateWithTime,
  arcSecToMin,
  arcSpatium,
  arcVelocitas,
  arcFormatLatitudeOrderCell,
  arcFormatLongitudeOrderCell,
} from '@src/core/extensions';
import { OrderDetailsStatusColors } from '@src/core/constants/order-status-colors';
import { OrderEntity, OrderStatusEnum, OrderStatusNames } from '@src/features/order/entities';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DeliveryModeEnum } from '../types';
import { setOrderFormData } from '@src/features/order/pages/orders-page/redux';
import { routesConfig } from '@src/core/routes';
import { useUnitType } from '@src/core/features/settings';


export const HistoryPageContent: React.FC = () => {
  const order = useSelector(selectSelectedHistoryOrder);
  const dispatch = useDispatch();
  // Subscribe so altitude/speed/distance values (arcSpatium/arcVelocitas) re-render
  // on a live unit switch (UNITS Standard R1).
  useUnitType();

  const getOrderInfo = () => {
    const orderData = order as Record<string, any>;

    return {
      id: orderData?.id || "N/A",
      createDate: orderData?.createDate,
      status: orderData?.status,
      packageContent: orderData?.packageContent || "N/A",
      clientName: orderData?.clientName || "N/A",
      deliveryLocation: orderData?.deliveryLocation,
      drone: orderData?.drone?.name,
      totalFlightTime: orderData?.totalFlightTime || 0,
      totalDistance: orderData?.totalDistance || 0,
      flightAltitude: orderData?.flightAltitude || 0,
      deliveryAltitude: orderData?.deliveryAltitude || 0,
      cruiseSpeed: orderData?.drone?.speed || 0,
      deliveryMode: DeliveryModeEnum[orderData?.deliveryMode as keyof typeof DeliveryModeEnum] || "N/A",
      longtitude: orderData?.deliveryLocation?.longitude || 0,
      latitude: orderData?.deliveryLocation?.latitude || 0,
    };
  };

  const orderInfo = getOrderInfo();

  const navigate = useNavigate();
  const onRepeat = () => {
    if (order) {
      const orderData = order as Record<string, any>;
      const orderToRepeat = Array.isArray(orderData.orders) ? orderData.orders[0] : orderData;
      
      dispatch(setOrderFormData(orderToRepeat as unknown as OrderEntity));
      dispatch(setMapLocation({
        latitude: Number(orderToRepeat.deliveryLocation?.latitude) || 0,
        longitude: Number(orderToRepeat.deliveryLocation?.longitude) || 0,
        address: orderToRepeat.deliveryLocation?.address || "",
      }));
      navigate(routesConfig.createOrder);
    }
  };


  return (
    <ArcBaseContent>
      <ArcStack
        py={3}
        px={5}
        paddingTop={2}
        width="100%"
        height="100%"
        spacing={2}
        sx={{ minHeight: 0, overflow: 'hidden' }}
      >

        <ArcStack direction="row" alignItems="center" justifyContent="space-between" flexShrink={0}>
          <ArcText.h3 fontWeight={500}>MISSION DETAILS</ArcText.h3>
          <ArcTooltip title='Repeat'>
            <ArcIconButton onClick={onRepeat}>
              <RefreshIcon />
            </ArcIconButton>
          </ArcTooltip>
        </ArcStack>

        <ArcStack direction="row" spacing={1} sx={{ flex: 1, minHeight: 0 }}>
          <ArcStack
            p='20px 35px'
            borderRadius={1}
            bgcolor="background.paper"
            width="50%"
            sx={{ minHeight: 0, overflowY: 'auto' }}
          >
            <ArcStack
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
            </ArcStack>

            <ArcStack
              spacing={3}
              sx={{ color: "white", marginTop: 1, marginBottom: 1 }}
            >
              <ArcStack direction="row" spacing={4}>
                <ArcStack spacing={3} flex={1}>
                  <ArcCellInfo label="Order" labelProps={{ fontSize: '14px' }} textProps={{ fontWeight: 400, fontSize: '16px' }}>#{orderInfo.id}</ArcCellInfo>

                  <ArcCellInfo label="Status" labelProps={{ fontSize: '14px' }} textProps={{ fontWeight: 400, fontSize: '16px' }}>
                    <ArcStack direction="row" alignItems="center" spacing={1}>
                      <ArcBox
                        width={8}
                        height={8}
                        borderRadius="50%"
                        bgcolor={
                          OrderDetailsStatusColors[
                      orderInfo.status?.toLowerCase() as OrderStatusEnum
                          ]
                        }
                      />
                      <ArcText color="white" fontSize={14} fontWeight={400}>
                        {OrderStatusNames[orderInfo.status?.toLowerCase() as OrderStatusEnum]}
                      </ArcText>
                    </ArcStack>
                  </ArcCellInfo>

                  <ArcCellInfo label="Duration" labelProps={{ fontSize: '14px' }} textProps={{ fontWeight: 400, fontSize: '16px' }}>
                    {arcSecToMin(orderInfo.totalFlightTime)}
                  </ArcCellInfo>

                  <ArcCellInfo label="Flight Altitude" labelProps={{ fontSize: '14px' }} textProps={{ fontWeight: 400, fontSize: '16px' }}>
                    {arcSpatium(orderInfo.flightAltitude, { fromSi: true })}
                  </ArcCellInfo>

                  <ArcCellInfo label="Cruise Speed" labelProps={{ fontSize: '14px' }} textProps={{ fontWeight: 400, fontSize: '16px' }}>
                    {arcVelocitas(orderInfo.cruiseSpeed, { fromSi: true })}
                  </ArcCellInfo>

                  <ArcCellInfo label="Package Content" labelProps={{ fontSize: '14px' }} textProps={{ fontWeight: 400, fontSize: '16px' }} sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                    {orderInfo.packageContent}
                  </ArcCellInfo>

                  <ArcCellInfo label="Latitude" labelProps={{ fontSize: '14px' }} textProps={{ fontWeight: 400, fontSize: '16px' }}>
                    {arcFormatLatitudeOrderCell(orderInfo.latitude)}
                  </ArcCellInfo>
                </ArcStack>

                <ArcStack spacing={3} flex={1}>
                  <ArcCellInfo label="Date" labelProps={{ fontSize: '14px' }} textProps={{ fontWeight: 400, fontSize: '16px' }}>
                    {arcDateWithTime(orderInfo.createDate?.toString() || "")}
                  </ArcCellInfo>

                  <ArcCellInfo label="Drone" labelProps={{ fontSize: '14px' }} textProps={{ fontWeight: 400, fontSize: '16px' }}>
                    {orderInfo?.drone || "N/A"}
                  </ArcCellInfo>

                  <ArcCellInfo label="Distance" labelProps={{ fontSize: '14px' }} textProps={{ fontWeight: 400, fontSize: '16px' }}>
                    {arcSpatium(orderInfo.totalDistance, { fromSi: true })}
                  </ArcCellInfo>

                  <ArcCellInfo label="Delivery Altitude" labelProps={{ fontSize: '14px' }} textProps={{ fontWeight: 400, fontSize: '16px' }}>  
                    {arcSpatium(orderInfo.deliveryAltitude, { fromSi: true })}
                  </ArcCellInfo>

                  <ArcCellInfo label="Delivery Mode" labelProps={{ fontSize: '14px' }} textProps={{ fontWeight: 400, fontSize: '16px' }}>
                    {orderInfo.deliveryMode}
                  </ArcCellInfo>

                  <ArcCellInfo label="Client Name" labelProps={{ fontSize: '14px' }} textProps={{ fontWeight: 400, fontSize: '16px' }} sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                    {orderInfo.clientName}
                  </ArcCellInfo>

                  <ArcCellInfo label="Longtitude" labelProps={{ fontSize: '14px' }} textProps={{ fontWeight: 400, fontSize: '16px' }}>
                    {arcFormatLongitudeOrderCell(orderInfo.longtitude)}
                  </ArcCellInfo>
                </ArcStack>
              </ArcStack>

              <ArcCellInfo label="Destination" labelProps={{ fontSize: '14px' }} textProps={{ fontWeight: 400, fontSize: '16px' }} sx={{ maxWidth: "300px" }}>
                {orderInfo.deliveryLocation?.address || "N/A"}
              </ArcCellInfo>

       
            </ArcStack>
          </ArcStack>
          <ArcStack width="50%" height="100%" borderRadius={1} sx={{ minHeight: 0, overflow: 'hidden' }}>
            <ArcGoogleMap hideToolbar hideMapControls>
              <Routes>
                <Route index element={<HistoryMap />} />
              </Routes>
            </ArcGoogleMap>
          </ArcStack>
        </ArcStack>
      </ArcStack>
    </ArcBaseContent>
  );
};  